import React, { useState, useMemo, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    TextField, 
    InputAdornment, 
    Box, 
    CircularProgress,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Snackbar,
    Alert,
    ButtonGroup,
} from '@mui/material';
import { Search, Clear, FilterList, ViewList, Map, LocationOn, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import CampaignCard from './CampaignCard';
import CampaignMapView from './CampaignMapView';
import BulkAdVariationDialog from './BulkAdVariationDialog';
import BulkAdVariationDeleteDialog from './BulkAdVariationDeleteDialog';
import { useCampaigns } from '../hooks/useCampaigns';
import { useNavigate, useLocation } from 'react-router-dom';
import { updateCampaignLocations } from '../utils/updateCampaignLocations';
import { VALID_CATEGORIES } from '../utils/standardizeCategories';

const CampaignListView: React.FC = () => {
    const { campaigns, loading, error } = useCampaigns();
    const [searchText, setSearchText] = useState('');
    const [selectedRegion, setSelectedRegion] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize viewMode from URL query parameter
    const queryParams = new URLSearchParams(location.search);
    const [viewMode, setViewMode] = useState<'list' | 'map'>(
        (queryParams.get('view') as 'list' | 'map') || 'list'
    );

    const [updatingLocations, setUpdatingLocations] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [isBulkAdVariationDialogOpen, setIsBulkAdVariationDialogOpen] = useState(false);
    const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

    // Update URL when view mode changes
    const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'list' | 'map' | null) => {
        if (newMode) {
            setViewMode(newMode);
            const params = new URLSearchParams(location.search);
            params.set('view', newMode);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        }
    };

    // Extract unique regions, categories, and countries from campaigns
    const { regions, categories, countries } = useMemo(() => {
        const regionsSet = new Set<string>();
        const categoriesSet = new Set<string>();
        const countriesSet = new Set<string>();

        campaigns.forEach(campaign => {
            if (campaign.region) regionsSet.add(campaign.region);
            if (campaign.category) categoriesSet.add(campaign.category);
            if (campaign.country) countriesSet.add(campaign.country);
        });

        return {
            regions: Array.from(regionsSet).sort(),
            categories: Array.from(categoriesSet).sort(),
            countries: Array.from(countriesSet).sort()
        };
    }, [campaigns]);

    // Filter campaigns based on search text and filters
    const filteredCampaigns = useMemo(() => {
        return campaigns.filter(campaign => {
            // Text search
            const matchesSearch = searchText.trim() === '' || 
                (campaign.centre_name?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                (campaign.city?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                (campaign.category?.toLowerCase().includes(searchText.toLowerCase()) || false);

            // Region filter
            const matchesRegion = !selectedRegion || campaign.region === selectedRegion;

            // Category filter
            const matchesCategory = !selectedCategory || campaign.category === selectedCategory;
            
            // Country filter
            const matchesCountry = !selectedCountry || campaign.country === selectedCountry;

            return matchesSearch && matchesRegion && matchesCategory && matchesCountry;
        });
    }, [campaigns, searchText, selectedRegion, selectedCategory, selectedCountry]);

    const handleCampaignClick = (campaignId: string) => {
        navigate(`/campaign/${campaignId}`);
    };

    const clearFilters = () => {
        setSelectedRegion('');
        setSelectedCategory('');
        setSelectedCountry('');
    };

    const handleUpdateLocations = async () => {
        setUpdatingLocations(true);
        try {
            await updateCampaignLocations(campaigns);
            showSnackbar('Locations updated successfully', 'success');
        } catch (error) {
            showSnackbar('Error updating locations', 'error');
        } finally {
            setUpdatingLocations(false);
        }
    };

    const handleCreateCampaign = () => {
        navigate('/campaign/new');
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(145deg, #1e40af 0%, #2563eb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Campaigns
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <ButtonGroup>
                        <Button
                            variant="contained"
                            onClick={handleCreateCampaign}
                        >
                            New Campaign
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setIsBulkAdVariationDialogOpen(true)}
                            startIcon={<AddIcon />}
                        >
                            Add Variation to Multiple
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setIsBulkDeleteDialogOpen(true)}
                            startIcon={<DeleteIcon />}
                        >
                            Delete Variation
                        </Button>
                    </ButtonGroup>
                    
                    {viewMode === 'map' && (
                        <Button
                            variant="outlined"
                            startIcon={<LocationOn />}
                            onClick={handleUpdateLocations}
                            disabled={updatingLocations}
                            size="small"
                        >
                            {updatingLocations ? 'Updating...' : 'Update Locations'}
                        </Button>
                    )}
                    
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        aria-label="view mode"
                        size="small"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewList />
                        </ToggleButton>
                        <ToggleButton value="map" aria-label="map view">
                            <Map />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* Search and Filters */}
            <Paper sx={{ p: 2, mb: 4 }}>
                <Grid container spacing={2}>
                    {/* Search Field */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search campaigns..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText ? (
                                    <InputAdornment position="end">
                                        <Clear 
                                            color="action" 
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setSearchText('')}
                                        />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Grid>

                    {/* Filters */}
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={selectedCountry}
                                label="Country"
                                onChange={(e) => setSelectedCountry(e.target.value)}
                            >
                                <MenuItem value="">All Countries</MenuItem>
                                {countries.map((country) => (
                                    <MenuItem key={country} value={country}>
                                        {country}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>Region</InputLabel>
                            <Select
                                value={selectedRegion}
                                label="Region"
                                onChange={(e) => setSelectedRegion(e.target.value)}
                            >
                                <MenuItem value="">All Regions</MenuItem>
                                {regions.map((region) => (
                                    <MenuItem key={region} value={region}>
                                        {region}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={selectedCategory}
                                label="Category"
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                <MenuItem value={VALID_CATEGORIES.FRANCHISE}>{VALID_CATEGORIES.FRANCHISE}</MenuItem>
                                <MenuItem value={VALID_CATEGORIES.CORPORATE}>{VALID_CATEGORIES.CORPORATE}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Active Filters */}
                    {(selectedRegion || selectedCategory || selectedCountry) && (
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FilterList color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    Active Filters:
                                </Typography>
                                {selectedCountry && (
                                    <Chip 
                                        label={`Country: ${selectedCountry}`}
                                        onDelete={() => setSelectedCountry('')}
                                        size="small"
                                    />
                                )}
                                {selectedRegion && (
                                    <Chip 
                                        label={`Region: ${selectedRegion}`}
                                        onDelete={() => setSelectedRegion('')}
                                        size="small"
                                    />
                                )}
                                {selectedCategory && (
                                    <Chip 
                                        label={`Category: ${selectedCategory}`}
                                        onDelete={() => setSelectedCategory('')}
                                        size="small"
                                    />
                                )}
                                <Chip 
                                    label="Clear All"
                                    onClick={clearFilters}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {loading ? (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: 200,
                    }}
                >
                    <CircularProgress size={40} thickness={4} />
                </Box>
            ) : error ? (
                <Paper 
                    sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        borderRadius: 3,
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #FCA5A5',
                    }}
                >
                    <Typography color="#B91C1C" align="center" sx={{ fontWeight: 500 }}>
                        Error loading campaigns: {error}
                    </Typography>
                </Paper>
            ) : filteredCampaigns.length === 0 ? (
                <Paper 
                    sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        borderRadius: 3,
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        No Results Found
                    </Typography>
                    <Typography color="text.secondary">
                        Try adjusting your search or filters
                    </Typography>
                </Paper>
            ) : viewMode === 'list' ? (
                <Grid 
                    container 
                    spacing={3}
                    sx={{
                        mt: 0,
                    }}
                >
                    {filteredCampaigns.map((campaign) => (
                        <Grid item xs={12} md={6} key={campaign.id}>
                            <CampaignCard
                                campaign={campaign}
                                onClick={() => campaign.id && handleCampaignClick(campaign.id)}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <CampaignMapView 
                    campaigns={filteredCampaigns}
                    onCampaignSelect={handleCampaignClick}
                />
            )}

            {/* Snackbar for notifications */}
            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Add BulkAdVariationDialog */}
            <BulkAdVariationDialog
                open={isBulkAdVariationDialogOpen}
                onClose={() => setIsBulkAdVariationDialogOpen(false)}
                campaigns={campaigns}
            />

            {/* Add BulkAdVariationDeleteDialog */}
            <BulkAdVariationDeleteDialog
                open={isBulkDeleteDialogOpen}
                onClose={() => setIsBulkDeleteDialogOpen(false)}
                campaigns={campaigns}
            />
        </Container>
    );
};

export default CampaignListView; 